import { useState } from "react";
import { Lock, Pencil, TriangleAlert, X } from "lucide-react";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "~/components/ui/sheet";
import { Skeleton } from "~/components/ui/skeleton";
import { Textarea } from "~/components/ui/textarea";
import useUser from "~/contexts/user";
import { ResourceLoadError } from "~/pages/manifold-nextgen/components/ResourceLoadError";
import { useAsyncMutation } from "~/pages/manifold-nextgen/hooks/useAsyncMutation";
import { evalCasePath, EVAL_SCORE_MAX, type EvalCase, type EvalCaseUpdate } from "~/services/evals";
import { apiMutate } from "~/utils/apiClient";
import { EvaluatorKind, LATENCY_EVALUATOR } from "./constants/evaluators";
import { DatasetNames, type DatasetRefs, TagChips } from "./primitives/chips";
import { formatDuration } from "./primitives/format";
import { CaseQuery, JsonBlock, Section, Spec, SpecRow } from "./primitives/layout";
import { evaluatorsForCase, splitCaseInputs } from "./selectors/cases";
import { useEvalCase } from "./useEvalApi";

interface CaseDetailSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  caseId: string | null;
  /** Catalog datasets by id, so the case names its datasets rather than listing uuids. */
  datasets: DatasetRefs;
  onSaved: () => void;
}

/**
 * A case's definition, and an editor for its metadata and two thresholds.
 *
 * `inputs` and `expectations` stay read-only: they are the scoring definition,
 * editing them bumps `version` and makes every past result incomparable, and they
 * are round-tripped from the YAML suites a JSONB editor here would diverge from.
 */
export const CaseDetailSheet = ({
  open,
  onOpenChange,
  caseId,
  datasets,
  onSaved,
}: CaseDetailSheetProps) => {
  const { data: evalCase, error, isLoading } = useEvalCase(caseId);
  const user = useUser();
  const [isEditing, setIsEditing] = useState(false);

  // Only the case's author may PATCH it — the API answers 403 to anyone else,
  // and a case with no recorded author has no creator to match, so it is not
  // editable at all. Reflecting that here means the button is absent rather
  // than offering an edit that fails on submit.
  const canEdit =
    !!evalCase?.created_by &&
    !!user?.email &&
    evalCase.created_by.toLowerCase() === user.email.toLowerCase();

  return (
    <Sheet
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          setIsEditing(false);
        }
        onOpenChange(next);
      }}
    >
      <SheetContent side="right" className="w-full gap-0 sm:max-w-xl">
        <SheetHeader className="border-b border-border pr-12">
          <SheetTitle className="font-mono text-sm break-all">
            {evalCase?.name ?? "Eval case"}
          </SheetTitle>
          <SheetDescription asChild>
            {evalCase ? (
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                {/* The version is an identifier, not prose: it is the thing a
                    result's `case_version` has to match to be comparable, so it
                    reads as a chip rather than as the head of a sentence. */}
                <Badge variant="secondary" className="font-mono tabular-nums">
                  v{evalCase.version}
                </Badge>
                <span className="truncate text-xs">
                  {evalCase.created_by ?? "Imported, no recorded author"}
                </span>
              </div>
            ) : (
              <span>The case definition.</span>
            )}
          </SheetDescription>
        </SheetHeader>

        {isLoading && !evalCase ? (
          <div className="flex flex-col gap-3 p-4" role="status" aria-label="Loading eval case">
            <Skeleton className="h-16 rounded-lg" />
            <Skeleton className="h-40 rounded-lg" />
          </div>
        ) : error || !evalCase ? (
          <ResourceLoadError error={error} resourceLabel="eval case" className="py-16" />
        ) : isEditing ? (
          <CaseEditForm
            evalCase={evalCase}
            onCancel={() => setIsEditing(false)}
            onSaved={() => {
              setIsEditing(false);
              onSaved();
            }}
          />
        ) : (
          <CaseReadView
            evalCase={evalCase}
            datasets={datasets}
            canEdit={canEdit}
            onEdit={() => setIsEditing(true)}
          />
        )}
      </SheetContent>
    </Sheet>
  );
};

const CaseReadView = ({
  evalCase,
  datasets,
  canEdit,
  onEdit,
}: {
  evalCase: EvalCase;
  datasets: DatasetRefs;
  canEdit: boolean;
  onEdit: () => void;
}) => {
  const { query, rest } = splitCaseInputs(evalCase.inputs);

  return (
    <>
      <div className="flex-1 overflow-y-auto p-4">
        <div className="flex flex-col gap-6">
          {evalCase.description && (
            <p className="text-sm leading-relaxed text-foreground">{evalCase.description}</p>
          )}

          {/* Selectors only: tags decide which runs cover the case, datasets
              what it reads. The two thresholds sit with the evaluators, being
              the rule that turns their output into a verdict. */}
          <Spec>
            <SpecRow label="Tags">
              <TagChips tags={evalCase.tags} />
            </SpecRow>
            <SpecRow label="Datasets">
              <DatasetNames ids={evalCase.dataset_ids} datasets={datasets} className="text-sm" />
            </SpecRow>
          </Spec>

          <Section title="Query">
            {query ? (
              <CaseQuery query={query} />
            ) : (
              <p className="text-sm text-muted-foreground">
                No question field — the inputs below are all this case sends.
              </p>
            )}
            {/* Anything the suite put in `inputs` beyond the question. Usually
                empty, so it costs nothing when there is nothing to say. */}
            {Object.keys(rest).length > 0 && <JsonBlock value={rest} />}
          </Section>

          <Section
            title="Evaluators"
            subtitle="The runner builds these from the expectations below; the case never names them directly."
          >
            <EvaluatorList evalCase={evalCase} />
          </Section>

          <Section
            title="Expectations"
            subtitle="The raw blob, including any key the runner ignores."
          >
            <JsonBlock value={evalCase.expectations} />
          </Section>
        </div>
      </div>

      <SheetFooter className="border-t border-border">
        {canEdit ? (
          <Button variant="outline" onClick={onEdit} className="w-full">
            <Pencil className="size-4" aria-hidden />
            Edit metadata
          </Button>
        ) : (
          <p className="flex items-center justify-center gap-1.5 text-xs text-muted-foreground">
            <Lock className="size-3.5" aria-hidden />
            Only the case&apos;s author can edit it
          </p>
        )}
      </SheetFooter>
    </>
  );
};

const CaseEditForm = ({
  evalCase,
  onCancel,
  onSaved,
}: {
  evalCase: EvalCase;
  onCancel: () => void;
  onSaved: () => void;
}) => {
  const [name, setName] = useState(evalCase.name);
  const [description, setDescription] = useState(evalCase.description ?? "");
  const [tagsText, setTagsText] = useState(evalCase.tags.join(", "));
  const [threshold, setThreshold] = useState(
    evalCase.pass_threshold == null ? "" : String(evalCase.pass_threshold)
  );
  const [maxLatency, setMaxLatency] = useState(
    evalCase.max_latency_seconds == null ? "" : String(evalCase.max_latency_seconds)
  );

  const parsedThreshold = threshold.trim() === "" ? null : Number(threshold);
  const parsedLatency = maxLatency.trim() === "" ? null : Number(maxLatency);
  const isThresholdValid = parsedThreshold == null || Number.isFinite(parsedThreshold);
  const isLatencyValid =
    parsedLatency == null || (Number.isInteger(parsedLatency) && parsedLatency > 0);
  const isNameValid = name.trim().length > 0;

  // Editing either threshold bumps the case's `version`, so results before and
  // after are no longer apples-to-apples. Saying so at the moment of the edit is
  // the only place a reviewer can act on it.
  const changesScoring =
    parsedThreshold !== evalCase.pass_threshold || parsedLatency !== evalCase.max_latency_seconds;

  const { execute, isPending } = useAsyncMutation(
    async () => {
      const body: EvalCaseUpdate = {
        name: name.trim(),
        description: description.trim() || null,
        tags: tagsText
          .split(",")
          .map((tag) => tag.trim())
          .filter(Boolean),
        pass_threshold: parsedThreshold,
        max_latency_seconds: parsedLatency,
      };
      return apiMutate<EvalCase>(evalCasePath(evalCase.id), "PATCH", body);
    },
    { errorMessage: "Could not save the case.", onSuccess: onSaved }
  );

  return (
    <>
      <div className="flex-1 overflow-y-auto p-4">
        <div className="flex flex-col gap-5">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="eval-case-name">Name</Label>
            <Input
              id="eval-case-name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              className="font-mono text-xs"
              aria-invalid={!isNameValid || undefined}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="eval-case-description">Description</Label>
            <Textarea
              id="eval-case-description"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              rows={3}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="eval-case-tags">Tags</Label>
            <Input
              id="eval-case-tags"
              value={tagsText}
              onChange={(event) => setTagsText(event.target.value)}
              placeholder="Clinicogenomic Counts, counts"
            />
            <p className="text-xs text-muted-foreground">
              Comma separated. Runs select cases by tag, so these decide which runs cover this case.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="eval-case-threshold">Pass threshold</Label>
              <Input
                id="eval-case-threshold"
                type="number"
                step="0.1"
                value={threshold}
                onChange={(event) => setThreshold(event.target.value)}
                placeholder="None"
                aria-invalid={!isThresholdValid || undefined}
              />
              <p className="text-xs text-muted-foreground">
                Blank means assertions alone decide pass or fail.
              </p>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="eval-case-latency">Latency limit (s)</Label>
              <Input
                id="eval-case-latency"
                type="number"
                min={1}
                value={maxLatency}
                onChange={(event) => setMaxLatency(event.target.value)}
                placeholder="None"
                aria-invalid={!isLatencyValid || undefined}
              />
              <p className="text-xs text-muted-foreground">Blank means the case is not timed.</p>
            </div>
          </div>

          {changesScoring && (
            <p className="rounded-md border border-orange-300 bg-orange-50 p-3 text-xs text-orange-800">
              This changes how the case is scored, so saving bumps it to version{" "}
              {evalCase.version + 1}. Results recorded against version {evalCase.version} stay
              comparable only with each other.
            </p>
          )}
        </div>
      </div>

      <SheetFooter className="flex-row border-t border-border">
        <Button variant="outline" onClick={onCancel} disabled={isPending} className="flex-1">
          <X className="size-4" aria-hidden />
          Cancel
        </Button>
        <Button
          onClick={() => void execute()}
          disabled={isPending || !isNameValid || !isThresholdValid || !isLatencyValid}
          className="flex-1"
        >
          {isPending ? "Saving…" : "Save changes"}
        </Button>
      </SheetFooter>
    </>
  );
};

/**
 * The evaluators this case resolves to, and what each does to the result. A
 * judge's score is what `score` averages; an assertion only flips `passed`. So a
 * case of assertions records no score and one of judges can fail only on its
 * threshold — both easy to create by accident, neither visible in the raw blob.
 */
/**
 * What a case is judged by, and what turns those judgements into a verdict.
 * Exported for its own tests: the verdict copy states the runner's rule, which is
 * not derivable from anything on the case.
 */
export const EvaluatorList = ({ evalCase }: { evalCase: EvalCase }) => {
  const evaluators = evaluatorsForCase(evalCase.expectations, evalCase.max_latency_seconds);
  const threshold = evalCase.pass_threshold;

  if (evaluators.length === 0) {
    return (
      <p className="flex items-start gap-1.5 rounded-md border border-orange-300 bg-orange-50 px-3 py-2 text-sm text-orange-800">
        <TriangleAlert className="mt-0.5 size-3.5 shrink-0" aria-hidden />
        No evaluator applies to this case. It will run, spend agent time and record no score or
        outcome.
      </p>
    );
  }

  const hasJudge = evaluators.some((entry) => entry.kind === EvaluatorKind.JUDGE);
  const hasAssertion = evaluators.some((entry) => entry.kind === EvaluatorKind.ASSERTION);

  return (
    <div className="flex flex-col gap-2">
      <ul className="divide-y divide-border overflow-hidden rounded-md border border-border">
        {evaluators.map((entry) => (
          <li key={entry.name} className="flex items-baseline justify-between gap-3 px-3 py-2">
            {/* The class name on hover: it is what the result's
                `evaluator_scores` is keyed by and what a reader greps for. */}
            <span className="truncate text-sm text-foreground" title={entry.name}>
              {entry.label}
            </span>
            {/* The latency assertion states its own bound rather than "pass or
                fail", which is the whole content of that row: every other
                evaluator's threshold lives in the expectations blob, and this
                one is a column on the case. */}
            <span className="shrink-0 text-xs text-muted-foreground">
              {entry.name === LATENCY_EVALUATOR && evalCase.max_latency_seconds != null
                ? `under ${formatDuration(evalCase.max_latency_seconds)}`
                : entry.kind === EvaluatorKind.JUDGE
                  ? `scores 1-${EVAL_SCORE_MAX}`
                  : "pass or fail"}
            </span>
          </li>
        ))}
      </ul>
      {/* What turns the rows above into an outcome. Not an evaluator, so it
          reads as the step after them rather than as another list item — and
          not a fact for the strip at the top of the panel either, since it is
          meaningless without the judges it applies to. */}
      {threshold != null ? (
        <p className="text-xs text-muted-foreground">
          {hasJudge ? (
            <>
              Passes when <span className="font-medium text-foreground">every</span> judge score
              reaches{" "}
              <span className="font-medium text-foreground tabular-nums">
                {threshold} / {EVAL_SCORE_MAX}
              </span>
              . The score above it is the mean, which can clear the bar when a single judge did not.
            </>
          ) : (
            <>
              Passes at {threshold} / {EVAL_SCORE_MAX}, but nothing here scores the answer, so the
              threshold never applies.
            </>
          )}
        </p>
      ) : (
        hasAssertion && (
          <p className="text-xs text-muted-foreground">
            No pass threshold, so the assertions alone decide pass or fail.
          </p>
        )
      )}
      {!hasJudge && (
        <p className="text-xs text-muted-foreground">
          No evaluator scores the answer, so this case records pass or fail and no score.
        </p>
      )}
      {!hasAssertion && threshold != null && (
        <p className="text-xs text-muted-foreground">
          No evaluator can fail this case on its own, so the threshold above is what decides it.
        </p>
      )}
      {!hasAssertion && threshold == null && (
        <p className="flex items-start gap-1.5 text-xs text-orange-800">
          <TriangleAlert className="mt-0.5 size-3.5 shrink-0" aria-hidden />
          Nothing here can fail this case, so it fails only when a judge errors or the run supplies
          its own threshold.
        </p>
      )}
    </div>
  );
};
